import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase/config';
import { API_ENDPOINTS, apiClient } from '../config/api';
import stateCityData from '../data/state_city.json';
import AddressAutocomplete from '../components/common/AddressAutocomplete';
import { formatUSPhoneInput, getUSPhoneError, toE164US } from '../utils/phone';
import './RegisterPage.css';
import './BrokerageRegistrationPage.css';

// ── Password rules (mirrors RegisterPage / BrokerageRegistrationPage) ────────
const passwordRules = [
  { id: 'length',  label: 'At least 8 characters',  test: (p) => p.length >= 8 },
  { id: 'upper',   label: 'One uppercase letter',    test: (p) => /[A-Z]/.test(p) },
  { id: 'lower',   label: 'One lowercase letter',    test: (p) => /[a-z]/.test(p) },
  { id: 'number',  label: 'One number',              test: (p) => /[0-9]/.test(p) },
  { id: 'special', label: 'One special character',   test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p) },
];
const isPasswordValid = (pwd) => passwordRules.every(r => r.test(pwd));

const validatePhone = (raw) => getUSPhoneError(raw);
const toE164 = (raw) => toE164US(raw) || raw;

const parseNameParts = (fullName) => {
  if (!fullName) return ['', ''];
  const parts = fullName.trim().split(/\s+/);
  return [parts[0] || '', parts.slice(1).join(' ') || ''];
};

// Resize an image File to at most 600×600 before upload — mirrors RegisterPage.js
const resizeImageToMax = (file, maxWidth = 600, maxHeight = 600) =>
  new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      const ratio = Math.min(maxWidth / width, maxHeight / height);
      if (ratio < 1) {
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => resolve(new File([blob], file.name, { type: 'image/jpeg' })),
        'image/jpeg',
        0.9
      );
    };
    img.src = url;
  });

// Format a brokerage's mailing address from the verify-license response fields
const formatBrokerageAddress = (firm) => {
  if (!firm) return '';
  const cityState = [firm.city, [firm.state, firm.zipCode].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [firm.addressLine1, cityState].filter(Boolean).join(', ');
};

const TCRegistrationScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const inviteData = location.state?.inviteData || {};

  const [inviteFirstName, inviteLastName] = parseNameParts(inviteData.name || '');

  // ── Identity ──────────────────────────────────────────────────────────────
  const [firstName, setFirstName]   = useState(inviteData.firstName || inviteFirstName);
  const [middleName, setMiddleName] = useState(inviteData.middleName || '');
  const [lastName, setLastName]     = useState(inviteData.lastName || inviteLastName);
  const [email, setEmail]           = useState(inviteData.email || '');
  const [phone, setPhone]           = useState(inviteData.phone || '');
  const [password, setPassword]               = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword]           = useState(false);
  const [showConfirmPwd, setShowConfirmPwd]       = useState(false);

  // ── Address ───────────────────────────────────────────────────────────────
  const [street, setStreet]   = useState(inviteData.street || '');
  const [city, setCity]       = useState(inviteData.city || '');
  const [addrState, setAddrState] = useState(inviteData.state || '');
  const [zipCode, setZipCode] = useState(inviteData.zipCode || '');

  // ── Optional profile photo ────────────────────────────────────────────────
  const [photo, setPhoto] = useState(null);
  const [photoFileName, setPhotoFileName] = useState('');

  // ── Brokerage Information (optional, verify-first) ───────────────────────
  const [licenceState, setLicenceState] = useState('');
  const [licenceId, setLicenceId]       = useState('');
  // 'idle' | 'validating' | 'valid' | 'invalid'
  const [validationState, setValidationState]     = useState('idle');
  const [validationMessage, setValidationMessage] = useState('');
  const [firmDetails, setFirmDetails] = useState(null);
  const [brokerageNarId, setBrokerageNarId] = useState('');

  // ── Form state ────────────────────────────────────────────────────────────
  const [fieldErrors, setFieldErrors] = useState({});
  const clearFieldErr = (key) => setFieldErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [legalModal, setLegalModal]       = useState(null);
  const [isSubmitting, setIsSubmitting]   = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  const isEmailFromInvite = !!inviteData.email;

  // ── Brokerage licence verification — auto-triggered on input, debounced
  // 1s, exactly mirroring BrokerageRegistrationPage.js / AgentVerificationPage.js's
  // established verify-license pattern (no manual "Verify" button on those
  // pages either). Takes explicit (license, state) args rather than reading
  // licenceId/licenceState from closure, so a debounced call scheduled a
  // keystroke ago can't fire against stale state.
  const debounceRef = useRef(null);

  const verifyLicence = useCallback(async (license, state) => {
    if (!state || license.trim().length < 3) return;
    setValidationState('validating');
    setValidationMessage('');
    setFirmDetails(null);
    try {
      const response = await apiClient.post(API_ENDPOINTS.verifyLicense, {
        licenseNumber: license.trim(),
        state,
        role: 'BROKERAGE',
      });
      const d = response.data?.data || response.data;

      if (d?.status !== 'valid') {
        setValidationState('invalid');
        setValidationMessage(
          d?.message || 'We could not verify this brokerage licence. Check the state and licence ID.'
        );
        return;
      }

      setFirmDetails({
        brokerageName: d.fullName || d.brokerageName || '',
        licenseType:   d.licenseType || d.brokerageLicenseType || '',
        addressLine1:  d.addressLine1 || '',
        city:          d.city || '',
        state:         d.state || state || '',
        zipCode:       d.zipCode || '',
      });
      setValidationState('valid');
      setValidationMessage('Brokerage licence verified successfully!');
    } catch (err) {
      setValidationState('invalid');
      setValidationMessage(err.response?.data?.message || 'Unable to verify licence. Please try again.');
    }
  }, []);

  // Editing State or Licence # after a verification resets the whole state
  // machine and hides the reveal tiles — prevents a stale name/address mismatch.
  const handleLicenceStateChange = (e) => {
    setLicenceState(e.target.value);
    setFirmDetails(null);
    setValidationState('idle');
    setValidationMessage('');
    clearFieldErr('licenceState');
    clearFieldErr('licenceId');
    if (debounceRef.current) clearTimeout(debounceRef.current);
  };
  const handleLicenceIdChange = (e) => {
    const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    setLicenceId(value);
    setFirmDetails(null);
    setValidationState('idle');
    setValidationMessage('');
    clearFieldErr('licenceId');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (licenceState && value.trim().length >= 3) {
      debounceRef.current = setTimeout(() => verifyLicence(value, licenceState), 1000);
    }
  };

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const resized = await resizeImageToMax(file);
      setPhoto(resized);
      setPhotoFileName(file.name);
    }
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!firstName.trim()) errs.firstName = 'First name is required';
    if (!lastName.trim())  errs.lastName  = 'Last name is required';
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errs.email = 'Enter a valid email address.';
    }
    const phoneErr = validatePhone(phone);
    if (phoneErr) errs.phone = phoneErr;

    if (!isPasswordValid(password)) errs.password = 'Password does not meet the requirements.';
    if (password !== confirmPassword) errs.confirmPassword = 'Passwords do not match.';

    if (!street.trim()) errs.street = 'Street address is required.';
    if (!city.trim()) errs.city = 'City is required.';
    if (!addrState.trim() || addrState.trim().length !== 2) errs.state = 'Enter a 2-letter state code.';
    const zipDigits = zipCode.replace(/\D/g, '');
    if (!zipDigits || (zipDigits.length !== 5 && zipDigits.length !== 9)) errs.zip = 'Enter a valid 5 or 9-digit ZIP code.';

    // Brokerage is required for TCs — state, licence # and a successful
    // verification are all mandatory (mirrors mobile TCRegistrationScreen;
    // only Brokerage NAR ID stays optional).
    if (!licenceState.trim()) {
      errs.licenceState = 'Brokerage state is required.';
    }
    if (!licenceId.trim()) {
      errs.licenceId = 'Brokerage licence # is required.';
    } else if (validationState !== 'valid') {
      errs.licenceId = 'Please verify the brokerage licence before continuing.';
    }

    if (!agreedToTerms) {
      setFieldErrors(errs);
      setStatus({ type: 'error', message: 'You must agree to the Terms of Service and Privacy Policy to register.' });
      return false;
    }
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      setStatus({ type: 'error', message: Object.values(errs)[0] });
      return false;
    }
    return true;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });
    if (!validate()) return;

    setIsSubmitting(true);
    setStatus({ type: 'info', message: 'Creating your account…' });

    const zipDigits = zipCode.replace(/\D/g, '');
    const isBrokerageVerified = validationState === 'valid' && !!firmDetails;

    try {
      const userCred = await createUserWithEmailAndPassword(auth, email.trim(), password);
      const firebaseToken = await userCred.user.getIdToken();
      const authHeaders = { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' };

      setStatus({ type: 'info', message: 'Completing registration…' });
      let backendResponse = null;
      let userId = null;
      try {
        const response = await apiClient.post(API_ENDPOINTS.register, {
          firebaseUid: userCred.user.uid,
          email: email.trim(),
          firstName: firstName.trim(),
          middleName: middleName.trim() || null,
          lastName: lastName.trim(),
          phone: toE164(phone),
          role: 'TRANSACTION_COORDINATOR',
          street: street.trim(),
          city: city.trim(),
          state: addrState.trim().toUpperCase(),
          postalCode: zipDigits,
          invitationKey: inviteData.key || '',
          // Brokerage-affiliation fields — only sent when the licence was verified.
          ...(isBrokerageVerified ? {
            companyLicenseNumber: licenceId.trim(),
            brokerLicenseState: licenceState,
            brokerageName: firmDetails.brokerageName || '',
            brokerageAddress: formatBrokerageAddress(firmDetails),
            brokerageLicenseType: firmDetails.licenseType || '',
            brokerageNarId: brokerageNarId.trim() || null,
          } : {}),
        }, { headers: authHeaders });
        backendResponse = response.data?.id ? response.data : (response.data?.data || response.data);
        userId = backendResponse?.id || backendResponse?.userId || userCred.user.uid;
      } catch (apiErr) {
        const isExists =
          apiErr.response?.status === 409 ||
          apiErr.response?.data?.message?.toLowerCase().includes('already exists');
        if (!isExists) {
          try { await userCred.user.delete(); } catch { /* cleanup */ }
          throw apiErr;
        }
        userId = userCred.user.uid;
      }

      if (photo && userId) {
        setStatus({ type: 'info', message: 'Uploading profile photo…' });
        try {
          const photoFormData = new FormData();
          photoFormData.append('photo', photo);
          await apiClient.post(API_ENDPOINTS.userPhoto(userId), photoFormData, {
            headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': undefined },
          });
        } catch { /* non-critical — user can add a photo later from My Profile */ }
      }

      if (inviteData.key) {
        try { await apiClient.post(API_ENDPOINTS.acceptUserInvite(inviteData.key), {}, { headers: authHeaders }); } catch { /* non-critical */ }
      }

      setStatus({ type: 'success', message: 'Registration complete! Redirecting to login…' });
      setTimeout(() => navigate('/login', {
        replace: true,
        state: { registrationSuccess: true, prefillEmail: email.trim() },
      }), 1500);
    } catch (err) {
      const code = err.code || '';
      if (code === 'auth/email-already-in-use') {
        setStatus({ type: 'error', message: 'This email is already registered. Please login instead.' });
      } else if (code === 'auth/weak-password') {
        setStatus({ type: 'error', message: 'Password is too weak. Please use a stronger password.' });
      } else if (code === 'auth/network-request-failed') {
        setStatus({ type: 'error', message: 'Network error. Please check your connection.' });
      } else {
        setStatus({ type: 'error', message: err.response?.data?.message || err.message || 'Registration failed. Please try again.' });
      }
      setIsSubmitting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="register-page">
      <div className="registration-container">

        <div className="header-section">
          <h1 className="title">Transaction Coordinator Registration</h1>
          <p className="subtitle">Create your Rialty account to start coordinating transactions</p>
        </div>

        <form onSubmit={handleSubmit}>

          {/* ── Identity ── */}
          <div className="form-row">
            <div className="brg-form-field">
              <label className="brg-field-label-above">First name <span className="brg-required">*</span></label>
              <input
                type="text"
                className={`form-input${fieldErrors.firstName ? ' brg-input-error' : ''}`}
                placeholder="First name"
                value={firstName}
                onChange={e => { setFirstName(e.target.value); clearFieldErr('firstName'); }}
              />
              {fieldErrors.firstName && <p className="brg-field-error">{fieldErrors.firstName}</p>}
            </div>
            <div className="brg-form-field">
              <label className="brg-field-label-above">Middle name</label>
              <input
                type="text"
                className="form-input"
                placeholder="Middle name (optional)"
                value={middleName}
                onChange={e => setMiddleName(e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="brg-field-label-above">Last name <span className="brg-required">*</span></label>
            <input
              type="text"
              className={`form-input${fieldErrors.lastName ? ' brg-input-error' : ''}`}
              placeholder="Last name"
              value={lastName}
              onChange={e => { setLastName(e.target.value); clearFieldErr('lastName'); }}
            />
            {fieldErrors.lastName && <p className="brg-field-error">{fieldErrors.lastName}</p>}
          </div>

          <div className="form-group">
            <label className="brg-field-label-above">Email address <span className="brg-required">*</span></label>
            <input
              type="email"
              className={`form-input${isEmailFromInvite ? ' input-readonly' : ''}${fieldErrors.email ? ' brg-input-error' : ''}`}
              placeholder="Email address"
              value={email}
              readOnly={isEmailFromInvite}
              onChange={e => { if (!isEmailFromInvite) { setEmail(e.target.value); clearFieldErr('email'); } }}
            />
            {isEmailFromInvite
              ? <p className="brg-field-helper">Auto-filled from invite</p>
              : fieldErrors.email && <p className="brg-field-error">{fieldErrors.email}</p>
            }
          </div>

          <div className="form-group">
            <label className="brg-field-label-above">Phone number <span className="brg-required">*</span></label>
            <input
              type="tel"
              className={`form-input${fieldErrors.phone ? ' brg-input-error' : ''}`}
              placeholder="Phone number"
              value={phone}
              onChange={e => { setPhone(formatUSPhoneInput(e.target.value)); clearFieldErr('phone'); }}
            />
            {fieldErrors.phone && <p className="brg-field-error">{fieldErrors.phone}</p>}
          </div>

          {/* ── Address ── */}
          <div className="form-group">
            <label className="brg-field-label-above">Street address <span className="brg-required">*</span></label>
            {/* Google autocomplete — same component + fill rules as
                RegisterPage's personal address. No restrictToStateCode: a
                personal address may be in any state. City/State/Zip stay
                editable, so a manual correction after selecting still works. */}
            <AddressAutocomplete
              value={street}
              onChange={(text) => { setStreet(text); clearFieldErr('street'); }}
              onAddressSelect={(parsed) => {
                if (parsed.street) setStreet(parsed.street);
                // Only fill when Google resolved a state — a partial result
                // (e.g. a route with no admin area) must not blank out values
                // the user already typed. Mirrors RegisterPage.
                if (parsed.stateCode) {
                  setCity(parsed.city || '');
                  setAddrState(parsed.stateCode.toUpperCase().slice(0, 2));
                  setZipCode((parsed.zip || '').replace(/\D/g, '').slice(0, 9));
                  ['street', 'city', 'state', 'zip'].forEach(clearFieldErr);
                }
              }}
              placeholder="Street address"
              inputClassName={`form-input${fieldErrors.street ? ' brg-input-error' : ''}`}
            />
            {fieldErrors.street && <p className="brg-field-error">{fieldErrors.street}</p>}
          </div>

          <div className="brg-form-row-3">
            <div className="brg-form-field">
              <label className="brg-field-label-above">City <span className="brg-required">*</span></label>
              <input
                type="text"
                className={`form-input${fieldErrors.city ? ' brg-input-error' : ''}`}
                placeholder="City"
                value={city}
                onChange={e => { setCity(e.target.value); clearFieldErr('city'); }}
              />
              {fieldErrors.city && <p className="brg-field-error">{fieldErrors.city}</p>}
            </div>
            <div className="brg-form-field">
              <label className="brg-field-label-above">State <span className="brg-required">*</span></label>
              <input
                type="text"
                className={`form-input${fieldErrors.state ? ' brg-input-error' : ''}`}
                placeholder="CA"
                value={addrState}
                maxLength={2}
                onChange={e => {
                  setAddrState(e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, 2));
                  clearFieldErr('state');
                }}
              />
              {fieldErrors.state && <p className="brg-field-error">{fieldErrors.state}</p>}
            </div>
            <div className="brg-form-field">
              <label className="brg-field-label-above">Zip code <span className="brg-required">*</span></label>
              <input
                type="text"
                className={`form-input${fieldErrors.zip ? ' brg-input-error' : ''}`}
                placeholder="ZIP"
                value={zipCode}
                maxLength={10}
                onChange={e => { setZipCode(e.target.value.replace(/\D/g, '').slice(0, 9)); clearFieldErr('zip'); }}
              />
              {fieldErrors.zip && <p className="brg-field-error">{fieldErrors.zip}</p>}
            </div>
          </div>

          {/* ── Password ── */}
          <div className="form-row" style={{ alignItems: 'flex-start' }}>
            <div className="brg-form-field">
              <label className="brg-field-label-above">Password <span className="brg-required">*</span></label>
              <div className="input-wrapper password-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className={`form-input${fieldErrors.password ? ' brg-input-error' : ''}`}
                  placeholder="Create a password"
                  value={password}
                  onChange={e => { setPassword(e.target.value); clearFieldErr('password'); }}
                />
                <button type="button" className="password-toggle-btn" onClick={() => setShowPassword(v => !v)} tabIndex="-1">
                  {showPassword
                    ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  }
                </button>
              </div>
              {fieldErrors.password && <p className="brg-field-error">{fieldErrors.password}</p>}
            </div>
            <div className="brg-form-field">
              <label className="brg-field-label-above">Confirm password <span className="brg-required">*</span></label>
              <div className="input-wrapper password-wrapper">
                <input
                  type={showConfirmPwd ? 'text' : 'password'}
                  className={`form-input${fieldErrors.confirmPassword ? ' brg-input-error' : ''}`}
                  placeholder="Re-enter password"
                  value={confirmPassword}
                  onChange={e => { setConfirmPassword(e.target.value); clearFieldErr('confirmPassword'); }}
                />
                <button type="button" className="password-toggle-btn" onClick={() => setShowConfirmPwd(v => !v)} tabIndex="-1">
                  {showConfirmPwd
                    ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  }
                </button>
              </div>
              {fieldErrors.confirmPassword && <p className="brg-field-error">{fieldErrors.confirmPassword}</p>}
            </div>
          </div>

          {password.length > 0 && (
            <div className="reg-requirements" style={{ marginTop: 8 }}>
              {passwordRules.map(rule => (
                <div key={rule.id} className={`reg-req-item ${rule.test(password) ? 'met' : ''}`}>
                  <span className="reg-req-icon">
                    {rule.test(password)
                      ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                      : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/></svg>
                    }
                  </span>
                  <span>{rule.label}</span>
                </div>
              ))}
            </div>
          )}

          {/* ── Optional profile photo ── */}
          <label className="photo-upload" style={{ marginTop: 16 }}>
            <div className="upload-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </div>
            <span className="upload-text">{photoFileName || 'Add photo (optional)'}</span>
            <input type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: 'none' }} />
          </label>

          {/* ── Brokerage Information (required) ── */}
          <div className="brg-section-label" style={{ marginTop: 28 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
            </svg>
            Brokerage Information <span className="brg-required">*</span>
          </div>

          <div className="brg-info-notice">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>Required — verify the brokerage you coordinate transactions for. Only the NAR ID below is optional.</span>
          </div>

          <div className="form-row" style={{ alignItems: 'flex-end' }}>
            <div className="brg-form-field">
              <label className="brg-field-label-above">State <span className="brg-required">*</span></label>
              <select
                value={licenceState}
                onChange={handleLicenceStateChange}
                className={`form-select${!licenceState ? ' placeholder' : ''}${fieldErrors.licenceState ? ' brg-input-error' : ''}`}
              >
                <option value="" disabled>Select state</option>
                {stateCityData.map(item => (
                  <option key={item.state} value={item.stateCode}>{item.state} ({item.stateCode})</option>
                ))}
              </select>
              {fieldErrors.licenceState && <p className="brg-field-error">{fieldErrors.licenceState}</p>}
            </div>
            <div className="brg-form-field">
              <label className="brg-field-label-above">Brokerage licence # <span className="brg-required">*</span></label>
              <div className="license-input-wrapper">
                <input
                  type="text"
                  placeholder="Brokerage licence ID"
                  value={licenceId}
                  onChange={handleLicenceIdChange}
                  maxLength={32}
                  className={`form-input${(validationState === 'invalid' || fieldErrors.licenceId) ? ' input-error' : ''}${validationState === 'valid' ? ' input-success' : ''}`}
                />
                {validationState === 'validating' && (
                  <span className="license-verifying">Verifying...</span>
                )}
              </div>
              {fieldErrors.licenceId && <p className="brg-field-error">{fieldErrors.licenceId}</p>}
            </div>
          </div>

          {validationState !== 'idle' && validationMessage && (
            <div className={`av-validation-row av-validation-${validationState}`}>
              <span className="av-validation-icon">
                {validationState === 'valid'      ? '✓'
                : validationState === 'validating' ? '…'
                :                                   '⚠'}
              </span>
              <span className="av-validation-text">{validationMessage}</span>
            </div>
          )}

          {firmDetails && validationState === 'valid' && (
            <>
              <div className="brg-success-notice" style={{ marginTop: 12 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <span>Licence verified. Brokerage details have been auto-populated from the licence registry.</span>
              </div>

              <div className="form-row">
                <div className="brg-form-field">
                  <label className="brg-field-label-above">Brokerage name</label>
                  <div className="brg-readonly-field">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
                    </svg>
                    <span>{firmDetails.brokerageName || '—'}</span>
                  </div>
                </div>
                <div className="brg-form-field">
                  <label className="brg-field-label-above">Licence type</label>
                  <div className="brg-readonly-field">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                    <span>{firmDetails.licenseType || '—'}</span>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="brg-field-label-above">Brokerage address</label>
                <div className="brg-readonly-field">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                  </svg>
                  <span>{formatBrokerageAddress(firmDetails) || '—'}</span>
                </div>
              </div>

              <div className="form-group">
                <label className="brg-field-label-above">Brokerage NAR ID</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="NAR ID (optional)"
                  value={brokerageNarId}
                  onChange={e => setBrokerageNarId(e.target.value)}
                />
              </div>
            </>
          )}

          {/* ── Terms ── */}
          <div className="reg-terms-row" style={{ marginTop: 20 }}>
            <input
              type="checkbox"
              id="agreeTerms"
              className="reg-terms-checkbox"
              checked={agreedToTerms}
              onChange={e => setAgreedToTerms(e.target.checked)}
            />
            <label htmlFor="agreeTerms" className="reg-terms-label">
              Yes, I understand and agree to the{' '}
              <button type="button" className="reg-terms-link" onClick={() => setLegalModal('terms')}>Rialty Terms of Service</button>
              {' '}and{' '}
              <button type="button" className="reg-terms-link" onClick={() => setLegalModal('privacy')}>Privacy Policy</button>.
            </label>
          </div>

          {status.message && (
            <div className={`status-message ${status.type}`}>{status.message}</div>
          )}

          <button
            type="submit"
            className="register-btn"
            style={{ marginTop: 16 }}
            disabled={isSubmitting || !agreedToTerms}
          >
            {isSubmitting ? (
              <>
                <span className="brg-spinner-dark brg-spinner-white" />
                Creating account…
              </>
            ) : 'Create account'}
          </button>

          <div className="auth-footer">
            <p>Already have an account? <Link to="/login" className="link">Login</Link></p>
          </div>
        </form>
      </div>

      {legalModal && (
        <div className="reg-legal-overlay" onClick={() => setLegalModal(null)}>
          <div className="reg-legal-modal" onClick={e => e.stopPropagation()}>
            <div className="reg-legal-header">
              <h2 className="reg-legal-title">
                {legalModal === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
              </h2>
              <button type="button" className="reg-legal-close" onClick={() => setLegalModal(null)} aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="reg-legal-body">
              {legalModal === 'terms' ? (
                <>
                  <p className="reg-legal-date">Last Updated: November 25, 2025</p>
                  <h3>1. Acceptance of Terms</h3>
                  <p>By accessing and using Rialty.ai ("the Platform"), you accept and agree to be bound by these Terms of Service. If you do not agree, please do not use the Platform.</p>
                  <h3>2. Description of Service</h3>
                  <p>Rialty.ai is an intelligent transaction management platform designed to streamline real estate workflows for professionals, buyers, and sellers.</p>
                  <h3>3. User Accounts and Registration</h3>
                  <p><strong>3.1 Account Creation:</strong> You agree to provide accurate, current, and complete information during registration.</p>
                  <p><strong>3.2 Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials.</p>
                  <h3>4. User Conduct</h3>
                  <p>You agree not to use the Platform for unlawful purposes, upload malicious code, interfere with the Platform, or impersonate others.</p>
                  <h3>5. Intellectual Property</h3>
                  <p>The Platform and its content are owned by Rialty.ai and protected by intellectual property laws.</p>
                  <h3>6. Governing Law</h3>
                  <p>These Terms shall be governed by applicable laws of the United States. Contact us at <strong>legal@rialty.ai</strong>.</p>
                </>
              ) : (
                <>
                  <p className="reg-legal-date">Last Updated: November 25, 2025</p>
                  <h3>1. Introduction</h3>
                  <p>Rialty.ai is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and protect your information.</p>
                  <h3>2. Information We Collect</h3>
                  <p>We collect name, email, phone, professional information, and usage data to provide our services.</p>
                  <h3>3. How We Use Your Information</h3>
                  <p>To provide and improve services, process transactions, communicate with you, and comply with legal obligations.</p>
                  <h3>4. Data Security</h3>
                  <p>We implement industry-standard security measures including encryption and access controls.</p>
                  <h3>5. Contact Us</h3>
                  <p>Questions? Contact us at <strong>privacy@rialty.ai</strong>.</p>
                </>
              )}
            </div>
            <div className="reg-legal-footer">
              <button type="button" className="reg-legal-agree-btn" onClick={() => { setAgreedToTerms(true); setLegalModal(null); }}>
                I have read and agree
              </button>
              <button type="button" className="reg-legal-dismiss-btn" onClick={() => setLegalModal(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TCRegistrationScreen;
